import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  TableFooter,
  Typography,
  Button,
  Box,
  Divider,
  Link,
  Drawer,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useState } from "react";
import { useTheme } from "@mui/material/styles";
import ReactMarkdown from 'react-markdown';
import DropdownMenu from "../DropdownMenu/DropdownMenu";
import ReusableModal from "../ReusableModal/ReusableModal";
import { getDeleteAttributes, getCitationData } from "../../api";
import { Select, MenuItem} from '@mui/material';
import { showLoader, hideLoader } from "../../store/loaderSlice";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/snackBarSlice";
import CloseIcon from '@mui/icons-material/Close';

// Type for column definition
interface Column {
  id: string;
  label: string;
  editable?: boolean; 
}

// Type for row data structure, allowing "id" to be a number
interface RowData {
  id: number; 
  [key: string]: string | number; 
}

interface Reference {
  node_id: string;
  file_id: number;
  page_label: number;
  score: number;
  content: string;
  file_name?: string;
}

interface EditableTableProps {
  search:string | ""
  fetchContractAttributes: (cwID: number, queVal: string,search:string, attributeFilter?:string) => void;
  CwId: any;
  tableKey: boolean;
  data: Record<string, any>[];
  isEditable: boolean; 
  tableType: string;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, rowId: number, field: string,tableType: string) => void; // Method to handle input changes
  columnSettings?: {
    aiReasoning?: boolean;
    references?: boolean;
    userAddedValue?: boolean;
    userAddedReasoning?: boolean;
  };
}

const EditableTable: React.FC<EditableTableProps> = ({
  search,
  fetchContractAttributes,
  CwId,
  tableKey,
  data,
  isEditable,
  handleInputChange,
  tableType,
  columnSettings = { 
    aiReasoning: true, 
    references: true,
    userAddedValue: true,
    userAddedReasoning: true
  }, // Default to showing columns if not provided
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isDataEmpty = !data || data.length === 0;
  const [open, setOpen] = useState(false);
  const [isCitationOpen, setIsCitationOpen] = useState(false);
  const [selectedReference, setSelectedReference] = useState<Reference | null>(null);
  const [loading, setLoading] = useState(false);
  const [citationUrl, setCitationUrl] = useState<string | null>(null);

  const [key, setKey] = useState("");
  const [tableRow,setTableRow] = useState([])

  const processAiValue = (value:any) => {
    if (!value) return value;
    
    try {
      const jsonData = JSON.parse(value);
      
      if (Array.isArray(jsonData)) {
        return jsonData.map((item, index) => {
          const entries = Object.entries(item)
            .map(([key, val]) => `**${key}:** ${val}`)
            .join('  \n');
          return `**Item ${index + 1}:**  \n${entries}`;
        }).join('  \n\n---  \n\n');
      } else if (typeof jsonData === 'object') {
        return Object.entries(jsonData)
          .map(([key, val]) => `**${key}:** ${val}`)
          .join('  \n');
      }
      
      return value;
    } catch (error) {
      return value;
    }
  };

  const handleOpen = (key:string,row:any) => {setOpen(true)
    setKey(key);
    setTableRow(row)
  };
	const handleClose = () => setOpen(false)

  const handleSaveConfirmed = async () => {
   await handleMenuSelect(key,tableRow)
    handleClose();
  }
  
  const handleMenuSelect = async (item:any, row:any) => {
    const payload = {
      form_data: [{
        answer_id: row?.answer_id,
        question_id: row?.question_id,
        user_added_value: row?.user_added_value || "",
        user_added_reasoning: row?.user_added_reasoning || ""
      }]
    };
    if (payload.form_data.length === 0) {
      return; 
    }
    await  DeleteAttributes(CwId,payload,row?.question_set)

	};



	const menuItems:any = [
		{ label: "Clear User Added Fields", id: "clear" },

	];

  const DeleteAttributes = async (cwID:number, payload:any,queVal:string) => {
    try {
      dispatch(showLoader());
      const response: any = await getDeleteAttributes(cwID,payload,queVal);
     if(response && response?.success){
       await fetchContractAttributes(CwId, queVal,search);
       dispatch(
        showSnackbar({
          message:  `${response.message}`,
          severity: "success",
        }),
      );
     }else{
      dispatch(
        showSnackbar({
          message:  `${response.message}`,
          severity: "error",
        }),
      );
     }
     
    } catch (error) {
      dispatch(
        showSnackbar({
          message:  `${error}`,
          severity: "error",
        }),
      );
    } finally {
      dispatch(hideLoader());
    }
  };


    // Helper function to calculate column width based on visible columns
  const getColumnWidth = (): string => {
    let visibleColumns = 2; // Base columns (Attribute, AI Value)
    if (columnSettings.aiReasoning) visibleColumns++;
    if (columnSettings.userAddedValue) visibleColumns++;
    if (columnSettings.userAddedReasoning) visibleColumns++;
    if (columnSettings.references) visibleColumns++;
    visibleColumns++; // Action column
    
    // Calculate percentage width (reserving 10% for action column)
    const remainingWidth = 90; // 100% - 10% for action column
    const columnWidth = Math.floor(remainingWidth / (visibleColumns - 1)); // -1 for action column
    
    return `${columnWidth}%`;
  };

  // Helper function to calculate colspan for footer
  const getColSpan = (): number => {
    let colSpan = 3; // Base columns (Attribute, AI Value, Actions)
    if (columnSettings.aiReasoning) colSpan++;
    if (columnSettings.userAddedValue) colSpan++;
    if (columnSettings.userAddedReasoning) colSpan++;
    if (columnSettings.references) colSpan++;
    return colSpan;
  };

const handleViewReference = async (reference: Reference) => {
    setSelectedReference(reference);
    setIsCitationOpen(true);
    setLoading(true);
    setCitationUrl(null);
    
    try {
      const response = await getCitationData(
        reference.file_id.toString(),
        (reference.page_label + 1).toString()
      );
      
      if (response && response.citation_url && response.citation_url.length > 0) {
        setCitationUrl(response.citation_url);
      } else {
        console.error('No citation URL found in response');
        dispatch(
          showSnackbar({
            message: 'Failed to load reference document',
            severity: 'error',
          })
        );
      }
    } catch (error) {
      console.error('Error fetching citation URL:', error);
      dispatch(
        showSnackbar({
          message: 'Error loading reference document',
          severity: 'error',
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Paper>
      {/* Table Container */}
      <TableContainer>
        <Table>
          {/* Table Header */}
          <TableHead style={{ backgroundColor: theme.palette.text.disabled  }}>
          <TableRow>
              <TableCell sx={{ width: getColumnWidth() ,fontSize:"1rem",fontWeight:"700"}}>Attribute</TableCell>
              <TableCell sx={{ width: getColumnWidth() ,fontSize:"1rem",fontWeight:"700"}}>AI Value</TableCell>
              {columnSettings.aiReasoning && (
                <TableCell sx={{ width: "15%" ,fontSize:"1rem",fontWeight:"700"}}>AI Reasoning</TableCell>
              )}
              {columnSettings.userAddedValue && (
                <TableCell sx={{ width: getColumnWidth() ,fontSize:"1rem",fontWeight:"700" }}>User Added Value</TableCell>
              )}
              {columnSettings.userAddedReasoning && (
                <TableCell sx={{ width: getColumnWidth() ,fontSize:"1rem",fontWeight:"700"}}>User Added Reasoning</TableCell>
              )}
              {columnSettings.references && (
                <TableCell sx={{ width: "15%" ,fontSize:"1rem",fontWeight:"700"}}>References</TableCell>
              )}
              <TableCell sx={{ width: "10%" ,fontSize:"1rem",fontWeight:"700"}}>Action(s)</TableCell>
            </TableRow>
          </TableHead>

 {/* Table Body */}
 <TableBody>
            {isDataEmpty ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body1" color="textSecondary" align="center">
                    No data found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data?.map((row:any) => (
                <TableRow key={row?.answer_id }>
                  <TableCell>{row?.attribute || "-"}</TableCell>
                  <TableCell>
                    {row?.ai_value ? (
                      <ReactMarkdown>{processAiValue(row.ai_value)}</ReactMarkdown>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  {columnSettings.aiReasoning && (
                    <TableCell>{row?.ai_reasoning || "-"}</TableCell>
                  )}
                  {columnSettings.userAddedValue && (
                    <TableCell>
                      {isEditable ? (
                      tableKey ? (
                        <TextField
                        value={row?.user_added_value || ""}
                        onChange={(e: any) => {
                          const newValue = e.target.value; 
                          handleInputChange(e, row.answer_id, "user_added_value", tableType);
                      }}
                      />
                        
                        ) : (
                          <Select
                          value={row?.user_added_value || ""}
                          onChange={(e: any) => {
                            handleInputChange(e, row.answer_id, "user_added_value", tableType);
                          }}
                          displayEmpty
                          fullWidth
                        >
                          <MenuItem value="Yes">Yes</MenuItem>
                          <MenuItem value="No">No</MenuItem>
                        </Select>
                    
                        )
                                              
                      ) : (
                        row.user_added_value || "-"
                      )}
                    </TableCell>
                  )}
                  {columnSettings.userAddedReasoning && (
                    <TableCell>
                      {isEditable ? (
                        <TextField
                          value={row?.user_added_reasoning || ""}
                          onChange={(e: any) => {
                            handleInputChange(e, row.answer_id, "user_added_reasoning", tableType);
                          }}
                        />
                      ) : (
                        row?.user_added_reasoning || "-"
                      )}
                    </TableCell>
                  )}
                  {columnSettings.references && (
                    <TableCell>
                      {row?.context && Array.isArray(row.context) && row.context.length > 0 ? (
                        <Box>
                          {row.context.map((ref: Reference, index: number) => (
                            <Link
                              key={ref.node_id}
                              component="button"
                              variant="body2"
                              onClick={() => handleViewReference(ref)}
                              sx={{ display: 'block', mb: 0.5, textAlign: 'left' }}
                            >
                              {ref.file_name ? `${ref.file_name} - P ${ref.page_label + 1}` : `Reference ${index + 1} - P ${ref.page_label + 1}`}
                            </Link>
                          ))}
                        </Box>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <DropdownMenu
                      menuItems={menuItems}
                      onSelect={(item) => handleMenuSelect(item, row)}
                      edit={isEditable}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>

          {/* Table Footer (Empty in this case) */}
          <TableFooter style={{ backgroundColor: theme.palette.text.disabled }}>
            <TableRow>
              <TableCell colSpan={getColSpan()}></TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Paper>
   

   
    <ReusableModal
        open={open}
        onClose={handleClose}
        title= {
          <Box>
          <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          
          >
            <Typography>Clear User Added Fields</Typography> 

              </Box>
              <Divider sx={{ width: "100%",marginTop: '20px'  }} />
              </Box>
        }
        content={
          <Box
          display="flex"
          >
           <Typography>Are you sure you want to clear user fields?</Typography> 

          </Box>
        }
        actions={
          <>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleSaveConfirmed}>
              Confirm
            </Button>
          </>
        }
      />

    {/* Citation Panel */}
    <Drawer
      anchor="right"
      open={isCitationOpen}
      onClose={() => setIsCitationOpen(false)}
      variant="temporary"
      sx={{
        '& .MuiDrawer-paper': {
          width: '50vw',
          boxSizing: 'border-box',
          top: '64px',
          height: 'calc(100% - 64px)',
        },
      }}
    >
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: theme.palette.background.paper,
      }}>
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}>
          <Box>
            <Typography variant="h6">Reference</Typography>
            {selectedReference && (
              <Typography variant="body2" color="text.secondary">
                {selectedReference.file_name || `File ID: ${selectedReference.file_id}`} - Page {selectedReference.page_label + 1}
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setIsCitationOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ 
          flex: 1,
          p: 2,
          overflowY: 'auto',
        }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}>
              <CircularProgress />
            </Box>
          ) : citationUrl ? (
            <Box sx={{ height: '100%' }}>
              <iframe
                src={citationUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                title={`${selectedReference?.file_name || `File ${selectedReference?.file_id}`} - Page ${selectedReference?.page_label !== undefined ? selectedReference.page_label + 1 : ''}`}
              />
            </Box>
          ) : (
            <Typography color="error">
              Unable to load the reference document. Please try again later.
            </Typography>
          )}
        </Box>
      </Box>
    </Drawer>
    </>
  );
};

export default EditableTable;
